
































































































































import Vue from 'vue';
import axios from 'axios';
import MultiComplete from '@/components/MultiComplete.vue';
import { DateTime } from "luxon";
import DateInput from "@/components/DateInput.vue";

interface DateRangeOptions {
  [year: number]: number[];
}

export type DateType = "week" | "calendar";

export interface ReportSpecBase {
  dateStyle: DateType;
  depot: string;
  accountCodes: Account[];
}

export interface ReportSpecByWeek extends ReportSpecBase {
  dateStyle: "week";
  from: {
    year: number,
    week: number,
  };
  to: {
    year: number,
    week: number,
  };
  weekday_from: number;
  weekday_to: number;
}

export interface ReportSpecByCalendar extends ReportSpecBase {
  dateStyle: "calendar";
  from: Date;
  to: Date;
}

export type ReportSpec = ReportSpecByWeek | ReportSpecByCalendar;

export interface Account {
  id: number;
  codes: string[];
  name: string;
}

const now = new Date();

export default Vue.extend({
  name: 'ReportControls',
  props: {
    allowCalendar: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: {
      default: {
        dateStyle: "week",
        depot: 'all',
        accountCodes: [],
        from: {
          year: now.getFullYear(),
          week: 1,
        },
        to: {
          year: now.getFullYear(),
          week: 1,
        },
        weekday_from: 0,
        weekday_to: 7,
      } as ReportSpec,
      type: Object as () => ReportSpec,
    },
    fromWeek1: {
      type: Boolean,
      default: false,
    },
    hideCustomerFilter: {
      type: Boolean,
      default: false,
    },
    showLastWeekOnMonday: {
      type: Boolean,
      default: false,
    },
    defaultToCurrentMonth: {
      type: Boolean,
      default: false,
    },
  },
  components: { MultiComplete, DateInput },
  data() {
    return {
      dateOptions: {} as DateRangeOptions,
      depots: [] as string[],
      accounts: [] as Account[],
      depot: 'all',
      from_year: now.getFullYear(),
      from_week: 1,
      from_date: DateTime.fromJSDate(now).set({ day: 1 }).toJSDate(),
      to_year: now.getFullYear(),
      to_week: 1,
      to_date: now,
      dateStyle: this.value.dateStyle ?? "week" as DateType,
      month_year: now.getFullYear(),
      month: undefined as number | undefined,
      weekday_from: now.getDay() + 1,
      weekday_to: now.getDay() + 1,
      accountFilter: [] as Account[],
      site: process.env.SITE,
    };
  },
  computed: {
    from_years(): number[] {
      return Object.keys(this.dateOptions).map((x) => parseInt(x, 10)).filter((y) => y <= this.to_year);
    },
    to_years(): number[] {
      return Object.keys(this.dateOptions).map((x) => parseInt(x, 10)).filter((y) => y >= this.from_year);
    },
    from_weeks(): number[] {
      return this.dateOptions[this.from_year]?.filter((w) => this.from_year < this.to_year || w <= this.to_week);
    },
    to_weeks(): number[] {
      return this.dateOptions[this.to_year]?.filter((w) => this.to_year > this.from_year || w >= this.from_week);
    },
    sortedAccounts(): Account[] {
      return this.accounts.slice().sort((a1, a2) => a1.name.localeCompare(a2.name));
    },
  },
  methods: {
    update(event?: any, keepMonthSelector: boolean = false) {
      let spec: ReportSpec;
      if (this.dateStyle === "calendar") {
        spec = {
          dateStyle: this.dateStyle,
          depot: this.depot,
          accountCodes: this.accountFilter,
          from: this.from_date,
          to: this.to_date,
        }
      } else {
        spec = {
          dateStyle: this.dateStyle,
          depot: this.depot,
          accountCodes: this.accountFilter,
          from: {
            year: this.from_year,
            week: this.from_week,
          },
          to: {
            year: this.to_year,
            week: this.to_week,
          },
          weekday_from: this.weekday_from,
          weekday_to: this.weekday_to,
        };
      }
      this.$emit('input', spec);
      if (!keepMonthSelector) {
        this.month = undefined;
      }
    },
    month_selected() {
      if (this.dateStyle === "calendar") {
        console.log(this.month)
        const start_of_month = DateTime.fromObject({year: this.month_year, month: (this.month! + 3) % 12 + 1, day: 1});
        this.from_date = start_of_month.toJSDate();
        this.to_date = start_of_month.endOf("month").toJSDate();
      } else {
        this.from_year = this.month_year;
        this.to_year = this.month_year;
        switch (this.month) {
          case 1:
            this.from_week = 1;
            this.to_week = 5;
            break;
          case 2:
            this.from_week = 6;
            this.to_week = 9;
            break;
          case 3:
            this.from_week = 10;
            this.to_week = 13;
            break;
          case 4:
            this.from_week = 14;
            this.to_week = 18;
            break;
          case 5:
            this.from_week = 19;
            this.to_week = 22;
            break;
          case 6:
            this.from_week = 23;
            this.to_week = 26;
            break;
          case 7:
            this.from_week = 27;
            this.to_week = 31;
            break;
          case 8:
            this.from_week = 32;
            this.to_week = 35;
            break;
          case 9:
            this.from_week = 36;
            this.to_week = 39;
            break;
          case 10:
            this.from_week = 40;
            this.to_week = 44;
            break;
          case 11:
            this.from_week = 45;
            this.to_week = 48;
            break;
          case 12:
            this.from_week = 49;
            this.to_week = this.dateOptions[this.month_year].length;
            break;
        }
      }
      this.update(undefined, true);
    },
  },
  async created() {
    const BASE_URL = (process.env.NODE_ENV === 'production') ? '/api' : '//localhost:8000';
    axios.get<{ depots: string[] }>(`${BASE_URL}/reports/depots/`).then((r) => this.depots = r.data.depots);
    // Don't need to wait for this to finish
    axios.get<{ results: Account[] }>(`${BASE_URL}/reports/accounts/`)
        .then((r) => this.accounts = r.data.results);
    // Need to wait for this though
    const response = await axios.get<{
      results: DateRangeOptions,
      current_year: number,
      current_week: number,
      current_month: number,
    }>(`${BASE_URL}/reports/year-weeks/`);
    this.dateOptions = response.data.results;
    this.from_year = response.data.current_year;
    if (this.fromWeek1) {
      this.from_week = 1;
    } else {
      this.from_week = response.data.current_week;
    }
    this.to_year = response.data.current_year;
    this.to_week = response.data.current_week;
    this.month_year = this.dateStyle === "calendar" ? now.getFullYear() : response.data.current_year;
    if (this.showLastWeekOnMonday && new Date().getDay() < 2) {
      if (this.from_week === 1) {
        this.from_year -= 1;
        const yearWeeks = this.dateOptions[this.from_year];
        this.from_week = yearWeeks[yearWeeks.length - 1];
      } else {
        this.from_week -= 1;
      }
      if (this.to_week === 1) {
        this.to_year -= 1;
        const yearWeeks = this.dateOptions[this.to_year];
        this.to_week = yearWeeks[yearWeeks.length - 1];
      } else {
        this.to_week -= 1;
      }
    }
    if (this.defaultToCurrentMonth) {
      this.month = response.data.current_month;
      this.month_selected();
    } else {
      this.update();
    }
  },
});
